import {
  arrayBuilderYesNoSchema,
  arrayBuilderYesNoUI,
} from 'platform/forms-system/src/js/web-component-patterns';
import {
  arrayOptions,
  capitalizeFirstLetter,
  createNewConditionName,
} from './utils';
import { getDuplicateConditionNames } from '../../../utils/duplicate-condition-names';
import { updateClaimTypeFromArray } from './claimType';
import { reconcileToxicExposureConditions } from '../../../utils/reconcile-toxic-exposure-conditions';
import { reconcileSelectedDisabilities } from '../../../utils/reconcile-supporting-evidence';
import ConfirmationNewAndRatedConditions from '../../../components/confirmationFields/ConfirmationNewAndRatedConditions';

const isOrphanSecondary = (item, fullData = {}) => {
  if (!item || item.cause !== 'SECONDARY') return false;
  const norm = s => (typeof s === 'string' ? s.trim().toLowerCase() : '');
  const target = norm(item.causedByDisability);
  if (!target) return true;

  const newNames = (fullData?.newDisabilities ?? [])
    .map(it =>
      norm(
        it?.condition
          ? createNewConditionName(it, true)
          : it?.newCondition ?? it?.name,
      ),
    )
    .filter(Boolean);

  const ratedNames = (fullData?.ratedDisabilities ?? [])
    .map(d => norm(d?.name))
    .filter(Boolean);

  return !(newNames.includes(target) || ratedNames.includes(target));
};

const summaryPage = {
  uiSchema: {
    'view:hasConditions': {
      ...arrayBuilderYesNoUI(arrayOptions, {}, { hint: null }),
      'ui:validations': [
        (errors, fieldData, formData) => {
          const items = formData?.newDisabilities || [];

          if (items.length === 0 && fieldData === false) {
            errors.addError(
              "You can't continue without adding a condition. Select Yes to add a condition.",
            );
          }

          const orphans =
            items.filter(d => isOrphanSecondary(d, formData)) || [];

          if (orphans.length > 0) {
            errors.addError(
              'A secondary condition is no longer linked to an existing condition. Please delete it, relink it to a current condition, or update its cause.',
            );
          }

          // Backstop for rows the item pages never checked, such as a resumed
          // in-progress form.
          const duplicates = getDuplicateConditionNames(formData);

          if (duplicates.length > 0) {
            const names = duplicates
              .map(name => `"${capitalizeFirstLetter(name)}"`)
              .join(', ');

            errors.addError(
              `You’ve added ${names} to your claim more than once. Please edit or delete a condition so each one has a different name.`,
            );
          }
        },
      ],
    },
    'ui:confirmationField': ConfirmationNewAndRatedConditions,
  },
  schema: {
    type: 'object',
    properties: {
      'view:hasConditions': arrayBuilderYesNoSchema,
    },
    required: ['view:hasConditions'],
  },
  updateFormData: (oldData, newData) => {
    const result = updateClaimTypeFromArray(oldData, newData);
    const oldLen = (oldData?.newDisabilities || []).length;
    const newLen = (result?.newDisabilities || []).length;

    if (newLen <= oldLen) {
      // Condition was deleted (newLen < oldLen) or renamed (newLen === oldLen).
      // Reconcile TE conditions first, then reconcile supporting evidence and POW.
      const afterTE = reconcileToxicExposureConditions(oldData, result);

      // For deletions, pass oldData=undefined to skip rename migration.
      const oldDataForRenames = newLen === oldLen ? oldData : undefined;
      return reconcileSelectedDisabilities(oldDataForRenames, afterTE);
    }
    return result;
  },
};

export default summaryPage;
